<template>
  <div class="bitts-breadcrumbs">
    <Breadcrumb>
      <template #separator>
        <FontAwesomeIcon :icon="['fak', 'chevron-right']" />
      </template>
      <BreadcrumbItem
        v-if="icon && showRoot"
        class="bitts-breadcrumbs__icon bitts-breadcrumb"
        :class="{ 'bitts-breadcrumbs__item--active': activeCrumbIndex === 0 }"
        @click="activeCrumbIndex !== 0 && $emit('root-clicked')"
      >
        <FontAwesomeIcon :icon="icon" class="mr-8" />
        <span
          class="bitts-breadcrumb"
          :class="
            activeCrumbIndex === 0
              ? 'bitts-breadcrumbs__item--active'
              : 'bitts-breadcrumbs__item--before'
          "
        >
          {{ crumbs[0]?.text }}
        </span>
      </BreadcrumbItem>
      <BreadcrumbItem
        v-for="crumb in beforeCrumbs"
        :key="crumb.name"
        :data-testid="`${crumb.name.toLowerCase()}-crumb`"
        class="bitts-breadcrumb bitts-breadcrumbs__item--before"
        @click="$emit('breadcrumb-clicked', crumb)"
      >
        {{ crumb.text }}
      </BreadcrumbItem>
      <BreadcrumbItem
        v-if="activeCrumb"
        :data-testid="`${activeCrumb.name.toLowerCase()}-crumb`"
        class="bitts-breadcrumb bitts-breadcrumbs__item--active"
      >
        {{ activeCrumb.text }}
      </BreadcrumbItem>
      <BreadcrumbItem
        v-for="crumb in afterCrumbs"
        :key="crumb.name"
        :data-testid="`${crumb.name.toLowerCase()}-crumb`"
        class="bitts-breadcrumb bitts-breadcrumbs__item--after"
        @click="$emit('breadcrumb-clicked', crumb)"
      >
        {{ crumb.text }}
      </BreadcrumbItem>
    </Breadcrumb>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Breadcrumb, BreadcrumbItem } from 'ant-design-vue';
import { computed } from 'vue';

type Crumb = {
  name: string;
  text: string;
};

type Props = {
  icon?: string[];
  crumbs?: Crumb[];
  activeCrumbIndex: number;
  lastCrumbIndex: number;
  showRoot?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  icon: () => ['fak', 'home'],
  crumbs: () => [],
  showRoot: true,
});

defineEmits<{
  'breadcrumb-clicked': [crumb: Crumb];
  'root-clicked': [];
}>();

const beforeCrumbs = computed(() =>
  props.crumbs.slice(props.showRoot ? 1 : 0, props.activeCrumbIndex),
);
const activeCrumb = computed(() => {
  // When showRoot is true and activeCrumbIndex is 0, don't show active crumb
  // because it's already displayed in the root breadcrumb
  if (props.showRoot && props.activeCrumbIndex === 0) {
    return null;
  }
  return props.crumbs.length > 0
    ? props.crumbs.at(props.activeCrumbIndex)
    : null;
});
const afterCrumbs = computed(() =>
  props.crumbs.slice(props.activeCrumbIndex + 1, props.lastCrumbIndex + 1),
);
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-breadcrumbs {
  @apply leading-6;

  .bitts-breadcrumbs__icon {
    @apply text-secondary-text-link cursor-pointer;

    &:hover:not(.bitts-breadcrumbs__item--active) {
      @apply text-secondary-text-link-hover;
    }

    &.bitts-breadcrumbs__item--active {
      @apply cursor-default;
    }
  }

  .bitts-breadcrumbs__item--before {
    @apply text-secondary-text-link cursor-pointer;
    &:hover {
      @apply text-secondary-text-link-hover;
    }
  }

  .bitts-breadcrumbs__item--after,
  .ant-breadcrumb li:last-child {
    @apply text-neutral-400;
  }

  .bitts-breadcrumbs__item--active,
  .ant-breadcrumb li:last-child.bitts-breadcrumbs__item--active {
    @apply text-neutral-text-weak;
  }

  .ant-breadcrumb {
    font-family: inherit;
  }

  .ant-breadcrumb-separator {
    @apply text-neutral-accent px-6 ms-0 me-0;
  }

  .bitts-breadcrumb:last-child {
    .ant-breadcrumb-separator {
      @apply hidden;
    }
  }
}
</style>
